import { useState } from "react";
import { motion } from "framer-motion";
import { Emblem } from "./Emblem";
import { LuxButton } from "./LuxButton";
import { EASE } from "./Reveal";
import { api, formatApiError } from "@/lib/api";

const SERVICES = ["Concierge", "Travel", "Dining", "Events", "Tickets", "Membership", "Other"];

export const EnquiryForm = ({ initialService = "Concierge", idPrefix = "enquiry" }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    service: initialService,
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/enquiries", form);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div
        data-testid={`${idPrefix}-success`}
        className="flex flex-col items-center py-10 text-center"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
      >
        <Emblem className="h-14 w-auto" />
        <div className="gold-hairline mt-7 w-24" />
        <p className="mt-7 max-w-sm font-serif text-2xl font-light italic leading-snug text-ivory">
          Thank you. A member of our concierge team will be in touch shortly.
        </p>
        <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-champagne/70">
          Discretion assured
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${idPrefix}-form`} className="mt-10">
      <div className="grid gap-x-10 gap-y-7 sm:grid-cols-2">
        <div>
          <label htmlFor={`${idPrefix}-first-name`} className="lux-label">First name</label>
          <input id={`${idPrefix}-first-name`} data-testid={`${idPrefix}-first-name-input`} required value={form.first_name} onChange={set("first_name")} className="lux-input" placeholder="Alexander" autoComplete="given-name" />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-last-name`} className="lux-label">Last name</label>
          <input id={`${idPrefix}-last-name`} data-testid={`${idPrefix}-last-name-input`} required value={form.last_name} onChange={set("last_name")} className="lux-input" placeholder="Rothwell" autoComplete="family-name" />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-email`} className="lux-label">Email</label>
          <input id={`${idPrefix}-email`} data-testid={`${idPrefix}-email-input`} type="email" required value={form.email} onChange={set("email")} className="lux-input" placeholder="you@example.com" autoComplete="email" />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-phone`} className="lux-label">Phone</label>
          <input id={`${idPrefix}-phone`} data-testid={`${idPrefix}-phone-input`} type="tel" value={form.phone} onChange={set("phone")} className="lux-input" placeholder="+44 ..." autoComplete="tel" />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-country`} className="lux-label">Country</label>
          <input id={`${idPrefix}-country`} data-testid={`${idPrefix}-country-input`} value={form.country} onChange={set("country")} className="lux-input" placeholder="United Kingdom" autoComplete="country-name" />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-service`} className="lux-label">Service interested in</label>
          <select id={`${idPrefix}-service`} data-testid={`${idPrefix}-service-select`} value={form.service} onChange={set("service")} className="lux-input">
            {SERVICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-7">
        <label htmlFor={`${idPrefix}-message`} className="lux-label">Message</label>
        <textarea id={`${idPrefix}-message`} data-testid={`${idPrefix}-message-input`} required rows={4} value={form.message} onChange={set("message")} className="lux-input resize-none" placeholder="How may we be of service?" />
      </div>
      {error && (
        <p data-testid={`${idPrefix}-error`} className="mt-5 text-[12px] font-light text-red-400/90">
          {error}
        </p>
      )}
      <div className="mt-9">
        <LuxButton type="submit" variant="gold" arrow disabled={busy} testId={`${idPrefix}-submit-button`}>
          {busy ? "Sending" : "Submit Enquiry"}
        </LuxButton>
      </div>
    </form>
  );
};
